import { useState, type FormEvent } from "react";

import AddIcon from "@mui/icons-material/Add";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  type CreateLogbookEntryPayload,
  type LogbookComplexity,
  type LogbookEntry,
  type LogbookKind,
  createLogbookEntry,
  deleteLogbookEntry,
  exportLogbookPdf,
  getLogbookStats,
  importLogbookFromAssignments,
  listLogbook,
} from "@/api/logbook";
import { PageHeader } from "@/components/PageHeader";
import { useAuthStore } from "@/store/authStore";

const KINDS: LogbookKind[] = [
  "REGULATION_CALL",
  "SMUR_DISPATCH",
  "TRANSFER",
  "TUTORING",
  "CONFERENCE",
  "FORMATION",
  "DPC_ACTION",
  "OTHER",
];
const COMPLEXITIES: LogbookComplexity[] = ["LOW", "MEDIUM", "HIGH", "CRITICAL"];

const COMPLEXITY_COLOR: Record<LogbookComplexity, "default" | "info" | "warning" | "error"> = {
  LOW: "default",
  MEDIUM: "info",
  HIGH: "warning",
  CRITICAL: "error",
};


function defaultFromTo(): { from: string; to: string } {
  const today = new Date();
  const from = new Date(today.getFullYear(), today.getMonth() - 11, 1);
  return {
    from: from.toISOString().slice(0, 10),
    to: today.toISOString().slice(0, 10),
  };
}


export function LogbookPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.me);

  const [{ from, to }, setRange] = useState(defaultFromTo());
  const [kindFilter, setKindFilter] = useState<LogbookKind | "">("");
  const [q, setQ] = useState("");

  const isPractitioner = Boolean(me?.id) && !me?.is_god;

  const entries = useQuery({
    queryKey: ["logbook", from, to, kindFilter, q],
    queryFn: () =>
      listLogbook({
        from,
        to,
        kind: kindFilter || undefined,
        q: q || undefined,
      }),
    enabled: isPractitioner,
  });

  const stats = useQuery({
    queryKey: ["logbook-stats", from, to],
    queryFn: () => getLogbookStats(from, to),
    enabled: isPractitioner,
  });

  const [showAdd, setShowAdd] = useState(false);
  const [importMessage, setImportMessage] = useState<string | null>(null);

  const importMutation = useMutation({
    mutationFn: () => importLogbookFromAssignments(from, to, "REGULATION_CALL"),
    onSuccess: (r) => {
      setImportMessage(t("logbook.imported", { imported: r.imported, skipped: r.skipped }));
      qc.invalidateQueries({ queryKey: ["logbook"] });
      qc.invalidateQueries({ queryKey: ["logbook-stats"] });
      setTimeout(() => setImportMessage(null), 4000);
    },
  });

  const exportMutation = useMutation({
    mutationFn: () => exportLogbookPdf(from, to),
    onSuccess: ({ blob, filename }) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteLogbookEntry(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["logbook"] });
      qc.invalidateQueries({ queryKey: ["logbook-stats"] });
    },
  });

  if (!isPractitioner) {
    return (
      <Box>
        <PageHeader title={t("logbook.title")} subtitle={t("logbook.subtitle")} helpTerm="mainCourante" />
        <Alert severity="info">{t("logbook.needPractitioner")}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title={t("logbook.title")}
        subtitle={t("logbook.subtitle")}
        actions={
          <>
            <Button
              startIcon={<PictureAsPdfIcon />}
              variant="outlined"
              color="secondary"
              onClick={() => exportMutation.mutate()}
              disabled={exportMutation.isPending}
            >
              {t("logbook.exportPdf")}
            </Button>
            <Button
              startIcon={<CloudDownloadIcon />}
              variant="outlined"
              onClick={() => importMutation.mutate()}
              disabled={importMutation.isPending}
            >
              {t("logbook.import")}
            </Button>
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              onClick={() => setShowAdd(true)}
            >
              {t("logbook.addEntry")}
            </Button>
          </>
        }
      />

      {importMessage && <Alert severity="success" sx={{ mb: 2 }}>{importMessage}</Alert>}

      {/* Period + filter */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 3, flexWrap: "wrap" }}>
        <TextField
          size="small"
          label={t("logbook.periodFrom")}
          type="date"
          value={from}
          onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small"
          label={t("logbook.periodTo")}
          type="date"
          value={to}
          onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
          InputLabelProps={{ shrink: true }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>{t("logbook.filterKind")}</InputLabel>
          <Select
            label={t("logbook.filterKind")}
            value={kindFilter}
            onChange={(e) => setKindFilter((e.target.value as LogbookKind) || "")}
          >
            <MenuItem value="">
              <em>{t("logbook.allKinds")}</em>
            </MenuItem>
            {KINDS.map((k) => (
              <MenuItem key={k} value={k}>
                {t(`logbook.kind.${k}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          label={t("logbook.search")}
          placeholder={t("logbook.searchPlaceholder")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          sx={{ minWidth: 220 }}
        />
      </Stack>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="caption" color="text.secondary">{t("logbook.totalEntries")}</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: "primary.main" }}>
                {stats.data?.total_entries ?? "—"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="caption" color="text.secondary">{t("logbook.totalCount")}</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: "secondary.main" }}>
                {stats.data?.total_count ?? "—"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="caption" color="text.secondary">
                {t("logbook.byKind")}
              </Typography>
              <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: "wrap", gap: 0.5 }}>
                {Object.entries(stats.data?.by_kind ?? {}).map(([k, v]) => (
                  <Chip
                    key={k}
                    size="small"
                    variant="outlined"
                    label={`${t(`logbook.kind.${k}`)} · ${v}`}
                  />
                ))}
                {Object.keys(stats.data?.by_kind ?? {}).length === 0 && (
                  <Typography variant="body2" color="text.secondary">—</Typography>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Entries */}
      {entries.isLoading ? (
        <CircularProgress />
      ) : (entries.data ?? []).length === 0 ? (
        <Alert severity="info">{t("logbook.noEntries")}</Alert>
      ) : (
        <Card variant="outlined">
          <List dense>
            {(entries.data ?? []).map((e) => (
              <EntryRow
                key={e.id}
                entry={e}
                onDelete={() => {
                  if (confirm(t("logbook.deleteConfirm"))) deleteMutation.mutate(e.id);
                }}
              />
            ))}
          </List>
        </Card>
      )}

      <AddEntryDialog
        open={showAdd}
        onClose={() => setShowAdd(false)}
        onCreated={() => {
          setShowAdd(false);
          qc.invalidateQueries({ queryKey: ["logbook"] });
          qc.invalidateQueries({ queryKey: ["logbook-stats"] });
        }}
      />
    </Box>
  );
}


function EntryRow({ entry, onDelete }: { entry: LogbookEntry; onDelete: () => void }) {
  const { t } = useTranslation();
  return (
    <ListItem
      divider
      secondaryAction={
        <Tooltip title={t("logbook.deleteConfirm")}>
          <IconButton edge="end" size="small" color="error" onClick={onDelete}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      }
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mr: 2, minWidth: 220 }}>
        <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
          {entry.date}
        </Typography>
        <Chip
          size="small"
          variant="outlined"
          label={t(`logbook.kind.${entry.kind}`)}
        />
      </Stack>
      <ListItemText
        primary={
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="body1">{entry.label}</Typography>
            <Chip size="small" color="primary" variant="outlined" label={`× ${entry.count}`} />
            {entry.duration_min !== null && (
              <Chip size="small" variant="outlined" label={`${entry.duration_min} min`} />
            )}
            {entry.complexity && (
              <Chip
                size="small"
                color={COMPLEXITY_COLOR[entry.complexity]}
                label={t(`logbook.complexity.${entry.complexity}`)}
              />
            )}
            {entry.assignment_id && (
              <Chip size="small" variant="outlined" color="success" label="auto" />
            )}
          </Stack>
        }
        secondary={entry.comment ?? undefined}
      />
    </ListItem>
  );
}


function AddEntryDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [kind, setKind] = useState<LogbookKind>("REGULATION_CALL");
  const [label, setLabel] = useState("");
  const [count, setCount] = useState(1);
  const [duration, setDuration] = useState<string>("");
  const [complexity, setComplexity] = useState<LogbookComplexity | "">("");
  const [comment, setComment] = useState("");
  const [serverError, setServerError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => {
      const payload: CreateLogbookEntryPayload = {
        date,
        kind,
        label: label.trim() || t(`logbook.kind.${kind}`),
        count,
        duration_min: duration ? parseInt(duration, 10) : null,
        complexity: complexity || null,
        comment: comment.trim() || null,
      };
      return createLogbookEntry(payload);
    },
    onSuccess: () => {
      // reset
      setLabel("");
      setCount(1);
      setDuration("");
      setComplexity("");
      setComment("");
      setServerError(null);
      onCreated();
    },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
      setServerError(typeof detail === "string" ? detail : (e as Error).message);
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    mutation.mutate();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("logbook.addEntry")}</DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={onSubmit}>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {serverError && <Alert severity="error">{serverError}</Alert>}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label={t("logbook.date")}
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ minWidth: 160 }}
              />
              <FormControl size="small" sx={{ flex: 1, minWidth: 180 }}>
                <InputLabel>{t("logbook.filterKind")}</InputLabel>
                <Select
                  label={t("logbook.filterKind")}
                  value={kind}
                  onChange={(e) => setKind(e.target.value as LogbookKind)}
                >
                  {KINDS.map((k) => (
                    <MenuItem key={k} value={k}>
                      {t(`logbook.kind.${k}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            <TextField
              label={t("logbook.label")}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              size="small"
              fullWidth
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label={t("logbook.count")}
                type="number"
                value={count}
                onChange={(e) => setCount(Math.max(0, parseInt(e.target.value || "0", 10)))}
                size="small"
                sx={{ width: 110 }}
                inputProps={{ min: 0 }}
              />
              <TextField
                label={t("logbook.duration")}
                type="number"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                size="small"
                sx={{ width: 130 }}
                inputProps={{ min: 0 }}
              />
              <FormControl size="small" sx={{ flex: 1, minWidth: 160 }}>
                <InputLabel>{t("logbook.complexityLabel")}</InputLabel>
                <Select
                  label={t("logbook.complexityLabel")}
                  value={complexity}
                  onChange={(e) => setComplexity((e.target.value as LogbookComplexity) || "")}
                >
                  <MenuItem value="">
                    <em>{t("logbook.noneComplexity")}</em>
                  </MenuItem>
                  {COMPLEXITIES.map((c) => (
                    <MenuItem key={c} value={c}>
                      {t(`logbook.complexity.${c}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            <TextField
              label={t("logbook.comment")}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              multiline
              minRows={2}
              maxRows={4}
              fullWidth
            />
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("common.cancel")}</Button>
        <Button
          variant="contained"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
        >
          {t("common.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
